import type { LabResultCounts, LabWorklistCounts } from '@dru/types';
import { cn } from '@dru/ui';
import { CircleAlert, PackageOpen } from 'lucide-react';

/**
 * Two separate questions, never one merged number: what specimens are still
 * on their way to the bench, and what is blocking a result from moving on.
 * Both counted on the server over everything in the caller's venues.
 * Who must act is named by role; nobody is invented as an assignee.
 */
export function LabStatusStrip({ tubes, results, canSign }: {
  tubes?: LabWorklistCounts['tubes'] | null;
  results?: LabResultCounts | null;
  /** This login validates and releases: "another reviewer" is not the right word for them. */
  canSign?: boolean;
}) {
  if (!tubes && !results) return null;
  const specimens = tubes ? [
    tubes.toReceive && `${tubes.toReceive} drawn, to receive`,
    tubes.inTransit && `${tubes.inTransit} in transit`,
    tubes.notDrawn && `${tubes.notDrawn} not drawn yet`,
  ].filter(Boolean) as string[] : [];
  const blocking = [
    tubes?.sentBack ? `${tubes.sentBack} tube${tubes.sentBack === 1 ? '' : 's'} with values sent back — re-enter` : null,
    results?.unacknowledgedCritical ? `${results.unacknowledgedCritical} critical value${results.unacknowledgedCritical === 1 ? '' : 's'} not acknowledged` : null,
    !canSign && results?.yourEntriesAwaitingOthers
      ? `${results.yourEntriesAwaitingOthers} of your entr${results.yourEntriesAwaitingOthers === 1 ? 'y waits' : 'ies wait'} for another reviewer (Pathologist)` : null,
  ].filter(Boolean) as string[];

  return (
    <div className="grid gap-2 md:grid-cols-2">
      <Group icon={<PackageOpen aria-hidden className="h-4 w-4" />} title="Specimens on their way"
             items={specimens} empty="None — every drawn tube is at the bench." />
      <Group icon={<CircleAlert aria-hidden className="h-4 w-4" />} title="Blocking" warn={blocking.length > 0}
             items={blocking} empty="Nothing is blocking a result." />
    </div>
  );
}

function Group({ icon, title, items, empty, warn }: {
  icon: React.ReactNode; title: string; items: string[]; empty: string; warn?: boolean;
}) {
  return (
    <section aria-label={title} className={cn('rounded-card border bg-white px-3 py-2 shadow-card',
      warn ? 'border-warning-600/40' : 'border-neutral-200')}>
      <h2 className={cn('flex items-center gap-1.5 text-caption font-medium', warn ? 'text-warning-600' : 'text-neutral-600')}>
        {icon}{title}
      </h2>
      {items.length
        ? <ul className="mt-0.5 space-y-0.5 text-small text-neutral-800">{items.map((t) => <li key={t}>{t}</li>)}</ul>
        : <p className="mt-0.5 text-small text-neutral-500">{empty}</p>}
    </section>
  );
}
